// src/app/services/report-preference.service.ts

import { Injectable } from '@angular/core';
import { SecureSessionService } from './secure-session.service';
import {
  FavouriteReportEntry,
} from './favourite-report.service';
import { NavFavouriteEntry } from './nav-favourite.service';
import {
  REPORT_STATE_SCHEMA_VERSION,
  ReportStateV1,
} from '../shared/utils/report-state.types';

// ── Storage key constants ──────────────────────────────────────────────────────
const STATE_PREFIX   = 'reportState:';
const FAV_PREFIX     = 'reportFavourites:';
const COL_PREFIX     = 'report-columns:';
const NAV_FAV_PREFIX = 'navFavourites:';

// ── Storage adapter interface ─────────────────────────────────────────────────

/**
 * Abstraction over the persistence layer.
 *
 * The default implementation writes to `localStorage`.
 * To switch to a backend API, create a class that implements this interface
 * and inject it via `ReportPreferenceService.setStorageAdapter()`.
 *
 * Switching the adapter does **not** require any changes in report components.
 */
export interface IPreferenceStorage {
  getItem(key: string): string | null;
  /** Returns false when the write failed (e.g. quota exceeded). */
  setItem(key: string, value: string): boolean;
  removeItem(key: string): void;
  /** Returns all known storage keys. */
  keys(): string[];
}

/** Default localStorage-backed adapter. */
class LocalStorageAdapter implements IPreferenceStorage {
  getItem(key: string): string | null {
    try { return localStorage.getItem(key); } catch { return null; }
  }
  setItem(key: string, value: string): boolean {
    try { localStorage.setItem(key, value); return true; } catch { return false; }
  }
  removeItem(key: string): void {
    try { localStorage.removeItem(key); } catch {}
  }
  keys(): string[] {
    try {
      const result: string[] = [];
      for (let i = 0; i < localStorage.length; i++) {
        const k = localStorage.key(i);
        if (k != null) result.push(k);
      }
      return result;
    } catch { return []; }
  }
}

// ── Service ───────────────────────────────────────────────────────────────────

/**
 * ReportPreferenceService
 * ──────────────────────
 * Single, centralised store for all report UI preferences.
 *
 * Storage key format
 * ------------------
 *   Report state  : `reportState:{userId}:{moduleName}:{reportId}`
 *   Favourites    : `reportFavourites:{userId}`
 *   Column prefs  : `report-columns:{userId}:{reportSlug}`
 *   Nav favourites: `navFavourites:{userId}`
 *
 * The service writes through an `IPreferenceStorage` adapter so the backend
 * can be swapped from localStorage to an API without touching report components.
 *
 * Logout behaviour
 * ----------------
 * Report preferences are intentionally **not** cleared on logout so they
 * survive re-authentication in the same browser.  Only authentication tokens
 * and session data are cleared on logout (see SharedService).
 * Each user's data is isolated by their userId so User B cannot see User A's
 * preferences.
 *
 * Backward-compatibility migration
 * ---------------------------------
 * Old keys written by the previous implementation (which used schoolId in the
 * key) are transparently read and re-saved under the new format on first access.
 */
@Injectable({ providedIn: 'root' })
export class ReportPreferenceService {
  private _storage: IPreferenceStorage = new LocalStorageAdapter();
  /** Set of userIds whose old-format keys have already been migrated. */
  private readonly _migratedUsers = new Set<string>();

  constructor(private session: SecureSessionService) {}

  // ── Storage adapter ────────────────────────────────────────────────────────

  /**
   * Replace the storage backend.
   * Call this once in `AppModule` (or a feature module) to use a backend API.
   *
   * @example
   * // In AppModule:
   * const prefSvc = inject(ReportPreferenceService);
   * prefSvc.setStorageAdapter(new MyApiStorageAdapter(http));
   */
  setStorageAdapter(adapter: IPreferenceStorage): void {
    this._storage = adapter;
  }

  // ── User context ───────────────────────────────────────────────────────────

  /** Return the current user's ID, or null when no session is active. */
  getCurrentUserId(): string | null {
    try {
      const raw = this.session.getSession();
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      const data = Array.isArray(parsed) ? parsed[0] : parsed;
      const id = String(data?.id ?? data?.userId ?? '').trim();
      return id || null;
    } catch {
      return null;
    }
  }

  // ── Report state ───────────────────────────────────────────────────────────

  /**
   * Persist a full state snapshot for the given report.
   * Only UI configuration should be included — never raw API data.
   *
   * Callers (ReportStateService) typically debounce this call.
   */
  saveReportState(
    userId: string,
    moduleName: string,
    reportId: string,
    state: ReportStateV1
  ): void {
    const key = this._stateKey(userId, moduleName, reportId);
    const json = this._safeStringify(state);
    if (json !== null) this._storage.setItem(key, json);
  }

  /**
   * Load the persisted state for a report.
   * Returns null when no state is saved, the data is corrupt, or the schema
   * version does not match.
   */
  getReportState(
    userId: string,
    moduleName: string,
    reportId: string
  ): ReportStateV1 | null {
    this._migrateIfNeeded(userId);
    const key = this._stateKey(userId, moduleName, reportId);
    return this._parseState(this._storage.getItem(key));
  }

  /** Remove the saved state for a specific report. */
  clearReportState(userId: string, moduleName: string, reportId: string): void {
    this._storage.removeItem(this._stateKey(userId, moduleName, reportId));
  }

  /** Remove all report state entries for the given user. */
  clearAllReportStates(userId: string): void {
    const prefix = `${STATE_PREFIX}${userId}:`;
    this._storage.keys()
      .filter(k => k.startsWith(prefix))
      .forEach(k => this._storage.removeItem(k));
  }

  // ── Column preferences ─────────────────────────────────────────────────────

  /** Storage key for column picker preferences. */
  columnKey(userId: string, reportSlug: string): string {
    return `${COL_PREFIX}${userId}:${reportSlug || 'default'}`;
  }

  // ── Favourites ─────────────────────────────────────────────────────────────

  /**
   * Add or replace a favourite entry for the given user.
   * If an entry with the same slug + module already exists it is replaced;
   * otherwise the entry is appended.
   */
  saveFavourite(userId: string, entry: FavouriteReportEntry): void {
    const current = this.getFavourites(userId);
    const idx = current.findIndex(
      f => f.slug === entry.slug && f.module === entry.module
    );
    const next = idx >= 0
      ? current.map((f, i) => (i === idx ? { ...f, ...entry } : f))
      : [...current, entry];
    this._writeFavourites(userId, next);
  }

  /** Return all favourites for the given user (empty array when none). */
  getFavourites(userId: string): FavouriteReportEntry[] {
    this._migrateIfNeeded(userId);
    const raw = this._storage.getItem(this._favKey(userId));
    if (!raw) return [];
    try {
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? (parsed as FavouriteReportEntry[]) : [];
    } catch {
      return [];
    }
  }

  /** Alias for saveFavourite — updates an existing entry in place. */
  updateFavourite(userId: string, entry: FavouriteReportEntry): void {
    this.saveFavourite(userId, entry);
  }

  /** Remove a single favourite entry (identified by slug + module). */
  removeFavourite(userId: string, slug: string, module: string): void {
    const next = this.getFavourites(userId).filter(
      f => !(f.slug === slug && f.module === module)
    );
    this._writeFavourites(userId, next);
  }

  // ── Left-navigation favourites ─────────────────────────────────────────────

  /**
   * Return the pinned left-navigation entries for the given user, in the order
   * the user arranged them (empty array when none / corrupt).
   */
  getNavFavourites(userId: string): NavFavouriteEntry[] {
    const raw = this._storage.getItem(this._navFavKey(userId));
    if (!raw) return [];
    try {
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? (parsed as NavFavouriteEntry[]) : [];
    } catch {
      return [];
    }
  }

  /**
   * Replace the whole pinned-navigation list for the given user.
   * The list is written as one array so ordering (drag-to-reorder) is preserved.
   */
  setNavFavourites(userId: string, entries: NavFavouriteEntry[]): void {
    const json = this._safeStringify(entries);
    if (json !== null) this._storage.setItem(this._navFavKey(userId), json);
  }

  // ── User preference lifecycle ──────────────────────────────────────────────

  /**
   * Remove all persisted report **states** (not favourites) for the given user.
   *
   * Intended for use by an explicit "Reset all preferences" UI action.
   * This is **not** called on logout — report preferences survive logout so
   * they are ready when the user returns.
   */
  clearCurrentUserPreferences(userId: string): void {
    this.clearAllReportStates(userId);
  }

  // ── Internal helpers ───────────────────────────────────────────────────────

  private _stateKey(userId: string, module: string, reportId: string): string {
    return `${STATE_PREFIX}${userId}:${module}:${reportId}`;
  }

  private _favKey(userId: string): string {
    return `${FAV_PREFIX}${userId}`;
  }

  private _navFavKey(userId: string): string {
    return `${NAV_FAV_PREFIX}${userId}`;
  }

  private _writeFavourites(userId: string, entries: FavouriteReportEntry[]): void {
    const json = this._safeStringify(entries);
    if (json !== null) this._storage.setItem(this._favKey(userId), json);
  }

  private _parseState(raw: string | null): ReportStateV1 | null {
    if (!raw) return null;
    try {
      const parsed = JSON.parse(raw) as ReportStateV1;
      if (parsed?._v !== REPORT_STATE_SCHEMA_VERSION) return null;
      return parsed;
    } catch {
      return null;
    }
  }

  private _safeStringify(value: unknown): string | null {
    try { return JSON.stringify(value); } catch { return null; }
  }

  // ── One-time key migration from old format ─────────────────────────────────

  private _migrateIfNeeded(userId: string): void {
    if (this._migratedUsers.has(userId)) return;
    this._migratedUsers.add(userId); // mark before doing work to prevent loops
    this._migrateFavourites(userId);
    this._migrateReportStates(userId);
    this._migrateColumnKeys(userId);
  }

  /**
   * Old favourite key: `favouriteReports_{schoolId}_{userId}` or
   * `favouriteReports__{userId}` (when schoolId was empty).
   * New key: `reportFavourites:{userId}`
   */
  private _migrateFavourites(userId: string): void {
    const newKey = this._favKey(userId);
    if (this._storage.getItem(newKey) !== null) return; // already on new format
    // Match keys like "favouriteReports_<anything>_<userId>"
    const pattern = new RegExp(`^favouriteReports_.*_${_escapeRegex(userId)}$`);
    const legacyKey = this._storage.keys().find(k => pattern.test(k));
    if (!legacyKey) return;
    const raw = this._storage.getItem(legacyKey);
    if (raw) this._storage.setItem(newKey, raw);
    // Keep old key in place so other code that might still reference it works.
  }

  /**
   * Old state key: `reportState:{schoolId}:{userId}:{module}:{slug}` (5 parts)
   * New state key: `reportState:{userId}:{module}:{slug}` (4 parts)
   */
  private _migrateReportStates(userId: string): void {
    this._storage.keys()
      .filter(k => k.startsWith(STATE_PREFIX))
      .forEach(k => {
        const parts = k.split(':');
        // Old format has 5 colon-separated segments
        if (parts.length !== 5) return;
        const [, , keyUserId, module, slug] = parts;
        if (keyUserId !== userId) return;
        const newKey = this._stateKey(userId, module, slug);
        if (this._storage.getItem(newKey) === null) {
          const raw = this._storage.getItem(k);
          if (raw) this._storage.setItem(newKey, raw);
        }
      });
  }

  /**
   * Old column key: `report-columns:{schoolId}_{userId}:{slug}`
   * New column key: `report-columns:{userId}:{slug}`
   */
  private _migrateColumnKeys(userId: string): void {
    this._storage.keys()
      .filter(k => k.startsWith(COL_PREFIX))
      .forEach(k => {
        // Strip the prefix to get "{scope}:{slug}"
        const rest = k.slice(COL_PREFIX.length); // "{scope}:{slug}"
        const colonIdx = rest.indexOf(':');
        if (colonIdx < 0) return;
        const scope = rest.slice(0, colonIdx);   // e.g. "1234_5678" or "5678"
        const slug = rest.slice(colonIdx + 1);   // e.g. "collection-summary"
        // Old format has underscore between schoolId and userId
        if (!scope.includes('_')) return;
        const parts = scope.split('_');
        const keyUserId = parts[parts.length - 1];
        if (keyUserId !== userId) return;
        const newKey = `${COL_PREFIX}${userId}:${slug}`;
        if (this._storage.getItem(newKey) === null) {
          const raw = this._storage.getItem(k);
          if (raw) this._storage.setItem(newKey, raw);
        }
      });
  }
}

function _escapeRegex(str: string): string {
  return str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}
